// src/components/account/LicenseModal.tsx
import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import apiClient from '../../api/client';

// Estende a interface Plugin para incluir purchaseId (usado na página de conta)
interface Purchase {
    purchaseId: number;
    purchaseDate: string;
    purchasePrice: number;
}

interface PluginWithPurchase {
    id: string;
    name: string;
    iconUrl?: string;
    purchases: Purchase[];
}

interface LicenseModalProps {
    isOpen: boolean;
    closeModal: () => void;
    plugin: PluginWithPurchase | null;
}


export function LicenseModal({ isOpen, closeModal, plugin }: LicenseModalProps) {
    const [licenses, setLicenses] = useState<{ [purchaseId: number]: { ip: string; lastUpdated: string | null } }>({});
    const [status, setStatus] = useState({ loading: false, error: '', success: '' });

    useEffect(() => {
        if (isOpen && plugin) {
            fetchLicenses();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, plugin]);

    const fetchLicenses = async () => {
        if (!plugin || !plugin.purchases || plugin.purchases.length === 0) return;

        try {
            setStatus({ loading: true, error: '', success: '' });
            
            // Fetch all licenses for all purchases
            const licensePromises = plugin.purchases.map(purchase =>
                apiClient.get(`/licenses/${purchase.purchaseId}`)
            );
            
            const responses = await Promise.all(licensePromises);
            
            const licensesMap: { [purchaseId: number]: { ip: string; lastUpdated: string | null } } = {};
            responses.forEach((response, index) => {
                const purchaseId = plugin.purchases[index].purchaseId;
                licensesMap[purchaseId] = {
                    ip: response.data.ip || '',
                    lastUpdated: response.data.lastUpdated || null
                };
            });
            
            setLicenses(licensesMap);
            setStatus({ loading: false, error: '', success: '' });
        } catch (err: any) {
            setStatus({ loading: false, error: err.response?.data?.error || 'Erro ao carregar licenças.', success: '' });
        }
    };

    const validateIp = (ip: string): boolean => {
        const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/;
        if (!ipRegex.test(ip)) return false;

        const parts = ip.split('.').map(Number);
        return parts.every(part => part >= 0 && part <= 255);
    };

    const handleIpChange = (purchaseId: number, newIp: string) => {
        setLicenses(prev => ({
            ...prev,
            [purchaseId]: {
                ...prev[purchaseId],
                ip: newIp
            }
        }));
    };

    const handleSaveAll = async () => {
        if (!plugin) return;

        // Collect all IPs to save
        const saves = plugin.purchases.map(purchase => {
            const license = licenses[purchase.purchaseId];
            const ip = license?.ip || '';
            return { purchaseId: purchase.purchaseId, ip: ip.trim() };
        });

        // Validate all IPs
        for (const save of saves) {
            if (!save.ip) {
                setStatus({ loading: false, error: `O IP da licença ${plugin.purchases.findIndex(p => p.purchaseId === save.purchaseId) + 1} não pode estar vazio.`, success: '' });
                return;
            }
            if (!validateIp(save.ip)) {
                setStatus({ loading: false, error: `Por favor, insira um IP válido para a licença ${plugin.purchases.findIndex(p => p.purchaseId === save.purchaseId) + 1}.`, success: '' });
                return;
            }
        }

        try {
            setStatus({ loading: true, error: '', success: '' });

            // Save all licenses
            const savePromises = saves.map(save =>
                apiClient.put(`/licenses/${save.purchaseId}`, { ip: save.ip })
            );

            await Promise.all(savePromises);

            // Update all licenses in state
            const updatedLicenses = { ...licenses };
            saves.forEach(save => {
                updatedLicenses[save.purchaseId] = {
                    ip: save.ip,
                    lastUpdated: new Date().toISOString()
                };
            });
            setLicenses(updatedLicenses);
            setStatus({ loading: false, error: '', success: 'Todas as licenças foram atualizadas com sucesso!' });
            setTimeout(() => {
                setStatus({ loading: false, error: '', success: '' });
            }, 2000);
        } catch (err: any) {
            let errorMessage = 'Erro ao atualizar as licenças.';
            if (err.response?.status === 429) {
                errorMessage = 'Você atualizou o IP recentemente. Aguarde alguns minutos.';
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error;
            }
            setStatus({ loading: false, error: errorMessage, success: '' });
        }
    };

    if (!plugin) return null;

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={closeModal}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-2xl transition-all">
                                {/* Header com gradiente */}
                                <div className="bg-gradient-to-r from-brand-blue to-blue-600 px-6 py-5 relative overflow-hidden">
                                    <div className="absolute inset-0 opacity-10">
                                        <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -mr-16 -mt-16"></div>
                                    </div>
                                    <div className="relative flex items-center gap-3">
                                        <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                                            <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </div>
                                        <div>
                                            <Dialog.Title as="h3" className="text-xl font-bold text-white">
                                                Gerenciar Licença
                                            </Dialog.Title>
                                            <p className="text-blue-100 text-sm">{plugin.name}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Conteúdo */}
                                <div className="p-6 space-y-5">
                                    {/* Informação */}
                                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
                                        <div className="flex gap-3">
                                            <div className="flex-shrink-0">
                                                <svg className="w-6 h-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div className="flex-1">
                                                <p className="text-sm text-blue-900 font-medium mb-1">
                                                    {plugin.purchases.length > 1
                                                        ? `Você possui ${plugin.purchases.length} licenças deste plugin`
                                                        : 'Configure o IP do servidor'
                                                    }
                                                </p>
                                                <p className="text-xs text-blue-700">
                                                    {plugin.purchases.length > 1
                                                        ? 'Configure o IP de cada licença individualmente.'
                                                        : 'Defina o endereço IP onde este plugin será utilizado.'
                                                    }
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Lista de Licenças */}
                                    <div className={`space-y-4 ${plugin.purchases.length > 2 ? 'max-h-96 overflow-y-auto custom-scrollbar' : ''}`}>
                                        {plugin.purchases.map((purchase, index) => {
                                            const license = licenses[purchase.purchaseId];
                                            const purchaseDate = new Date(purchase.purchaseDate);

                                            return (
                                                <div key={purchase.purchaseId} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                                                    <div className="flex items-center justify-between mb-3">
                                                        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                                            <span className="w-6 h-6 bg-brand-blue text-white rounded-full flex items-center justify-center text-xs font-bold">
                                                                {index + 1}
                                                            </span>
                                                            Licença #{index + 1}
                                                        </h4>
                                                        <div className="text-xs text-gray-500">
                                                            {purchaseDate.toLocaleDateString('pt-BR')}
                                                        </div>
                                                    </div>

                                                    {license?.lastUpdated && (
                                                        <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                            </svg>
                                                            Última atualização: {new Date(license.lastUpdated).toLocaleString('pt-BR')}
                                                        </div>
                                                    )}

                                                    <div className="space-y-3">
                                                        <div className="relative">
                                                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                                <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                                                                </svg>
                                                            </div>
                                                            <input
                                                                type="text"
                                                                value={license?.ip || ''}
                                                                onChange={(e) => handleIpChange(purchase.purchaseId, e.target.value)}
                                                                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-blue focus:border-transparent text-gray-900 placeholder-gray-400 transition-all text-sm"
                                                                placeholder="192.168.1.1"
                                                                disabled={status.loading}
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Mensagens de status */}
                                    {status.error && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                                            <svg className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <p className="text-sm text-red-700">{status.error}</p>
                                        </div>
                                    )}

                                    {status.success && (
                                        <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
                                            <svg className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <p className="text-sm text-green-700">{status.success}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Footer com botões */}
                                <div className="bg-gray-50 px-6 py-4 flex justify-between items-center border-t border-gray-200">
                                    <div className="text-xs text-gray-500">
                                        {plugin.purchases.length > 1 ? 'Salve todas as licenças de uma vez' : 'Salve a configuração do IP'}
                                    </div>
                                    <div className="flex gap-3">
                                        <button
                                            type="button"
                                            className="px-5 py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-brand-blue to-blue-600 rounded-lg hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-blue disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
                                            onClick={handleSaveAll}
                                            disabled={status.loading}
                                        >
                                            {status.loading ? (
                                                <>
                                                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                    </svg>
                                                    Salvando...
                                                </>
                                            ) : (
                                                <>
                                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                    </svg>
                                                    Salvar
                                                </>
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            className="px-5 py-2.5 text-sm font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                            onClick={closeModal}
                                            disabled={status.loading}
                                        >
                                            Fechar
                                        </button>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}